import { useEffect, useState } from "react";
import { Wizard } from "./components/Wizard";
import { TripPage } from "./components/TripPage";
import { InsightsPage } from "./components/InsightsPage";
import { getMyTrips } from "./lib/store";

// Tiny path-based router (SPA fallback handled by Pages via public/_redirects).
function useRoute() {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const navigate = (to: string) => {
    window.history.pushState({}, "", to);
    setPath(to);
    window.scrollTo(0, 0);
  };
  return { path, navigate };
}

export function App() {
  const { path, navigate } = useRoute();

  const tripMatch = path.match(/^\/t\/([^/]+)$/);

  return (
    <div className="min-h-full">
      <header className="sticky top-0 z-10 border-b border-slate-100 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-lg font-extrabold text-ink">
            <span className="grid h-8 w-8 place-items-center rounded-xl bg-brand-600 text-white">✈</span>
            TravelHub
          </button>
          <nav className="flex items-center gap-1 text-sm font-medium">
            <button onClick={() => navigate("/")} className="rounded-lg px-3 py-1.5 text-slate-600 hover:bg-slate-100">Plan a trip</button>
            <button onClick={() => navigate("/insights")} className="rounded-lg px-3 py-1.5 text-slate-600 hover:bg-slate-100">Insights</button>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8">
        {tripMatch ? (
          <TripPage slug={tripMatch[1]} />
        ) : path === "/insights" ? (
          <InsightsPage />
        ) : (
          <Home navigate={navigate} />
        )}
      </main>

      <footer className="mx-auto max-w-5xl px-4 py-8 text-center text-xs text-slate-400">
        Built on Cloudflare + Firecrawl · itineraries are estimates — always confirm live prices via the booking links.
      </footer>
    </div>
  );
}

function Home({ navigate }: { navigate: (to: string) => void }) {
  const myTrips = getMyTrips();
  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
          Plan a trip everyone in the group actually wants.
        </h1>
        <p className="mx-auto mt-3 max-w-2xl text-slate-500">
          Tell TravelHub your objectives, who's coming, your budget and preferences.
          Get a customised day-by-day itinerary that balances the whole party — with one-click price comparison and a shareable link.
        </p>
      </div>

      {myTrips.length > 0 && (
        <div className="card">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-400">Your recent trips</h3>
          <div className="flex flex-wrap gap-2">
            {myTrips.map((t) => (
              <button key={t.slug} onClick={() => navigate(`/t/${t.slug}`)}
                className="rounded-xl bg-slate-50 px-3 py-2 text-left text-sm ring-1 ring-slate-100 hover:ring-brand-300">
                <span className="font-semibold">{t.title}</span>
                <span className="block text-xs text-slate-400">{t.destination}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <Wizard onComplete={(slug) => navigate(`/t/${slug}`)} />
    </div>
  );
}
